import { useMemo, useState } from 'react'
import { DatePicker, Select, Space, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { queryKeys } from '@/shared/api/queryKeys'
import type { OperationResult } from '@/shared/api/operationResult'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { OperationResultAlert } from '@/shared/ui/OperationResultAlert'
import { PageHeader } from '@/shared/ui/PageHeader'
import { DataTable } from '@/shared/ui/table/DataTable'
import { RowActions } from '@/shared/ui/table/RowActions'
import { actorLabel } from '@/features/identity/actorName'
import { useActorDirectory } from '@/features/identity/useActorDirectory'
import { LeaveDecisionModal, type LeaveAction } from '@/features/hr/LeaveDecisionModal'
import { LEAVE_STATE_COLOR } from '@/features/hr/leaveState'
import {
  LEAVE_REQUEST_STATES,
  canCancelLeave,
  fetchLeaveRequestPage,
  isPending,
  type LeaveRequest,
  type LeaveRequestState,
} from '@/features/hr/leaveApi'

const DATE_FORMAT = 'YYYY-MM-DD'

/**
 * Ta'til so'rovlari — QARORLAR ekrani.
 *
 * <h2>⚠️ SO'ROV BU YERDA YOZILMAYDI</h2>
 * `POST /persons/{id}/leave-requests` xodim id'sini talab qiladi, ya'ni
 * so'rovning tabiiy joyi — XODIM KARTOCHKASI. Bu yerda odam
 * tanlagichini qo'yish ikkinchi yo'l ochardi va ikkalasi vaqt o'tib
 * ajralib ketardi. Sahifa buni matn bilan aytadi, tugma chizmaydi.
 *
 * <h2>⚠️ ORALIQ FILTRI — KESISHUV, ICHIGA OLISH EMAS</h2>
 * Server `from` ni `ends_on >= from`, `to` ni `starts_on <= to` deb
 * qo'llaydi. Ya'ni oraliqqa bir kuni tushgan so'rov ham ro'yxatga
 * kiradi. Bu izohda ochiq yozilgan — aks holda operator «to'liq shu
 * oyga tegishlilar» deb o'ylardi.
 *
 * <h2>⚠️ STANDART ORALIQ YO'Q — VA BU O'LCHANGAN QARORNING NATIJASI</h2>
 * 2026-08-16 gacha bu yerda `defaultLeaveRange()` turardi, ya'ni
 * JORIY OY. Tashqi tester buni nuqson deb yozgan (BUG-051: «BU MENUDA
 * HECH NARSA CHIQMAYABDI») va Staging'da o'lchov uni TASDIQLADI:
 *
 * <pre>
 * POST .../persons/{id}/leave-requests   → 201, holat PENDING
 *      (10–12 sentabr — ODAM TA'TILNI OLDINDAN so'raydi)
 *
 * GET  /api/hr/leave-requests?state=PENDING&from=2026-08-01&to=2026-08-31
 *      → total = 0     ← panel AYNAN shuni so'rardi, ekran BO'SH
 * GET  /api/hr/leave-requests?state=PENDING
 *      → total = 1     ← so'rov BOR edi, ko'rsatilmasdi
 * </pre>
 *
 * <p>⚠️ Sabab filtrning O'ZIDA emas — u to'g'ri ishlaydi. Sabab
 * STANDART QIYMATDA: ta'til so'rovi tabiatan KELAJAKKA yoziladi, joriy
 * oy oynasi esa aynan kelajakni kesib tashlaydi. Ya'ni tasdiqlashi
 * kerak bo'lgan odam yangi so'rovni PRINSIPIAL ravishda ko'rmasdi.
 *
 * <p>⚠️ Nega oynani KENGAYTIRISH emas, OLIB TASHLASH. Bu ekranning
 * vazifasi — QAROR qabul qilish, va standart holat `PENDING`. Kutib
 * turgan so'rov qachon boshlanishidan qat'i nazar qaror talab qiladi:
 * kelasi yilgi ham, o'tib ketgani ham (u ayniqsa muhim — muddati
 * o'tgan so'rov). Har qanday belgilangan oyna «qaysi biri qolib
 * ketdi» degan savolni qaytadan tug'dirardi.
 *
 * <p>⚠️ Ro'yxat baribir SAHIFALANADI (`DataTable`), ya'ni oraliqsiz
 * so'rov «butun tarixni tortish» degani emas. Bu `AttendanceEventsPage`
 * dan farqli holat: u yerda oyna MAJBURIY, chunki `attendance.event`
 * har o'tishda o'sadi; `hr.leave_request` esa odam yozadigan jadval.
 *
 * <p>Tanlagich joyida turibdi — operator xohlagan oynani o'zi qo'yadi.
 *
 * <h2>✅ «YOPILGANLAR QOIDASI» BU YERDA ALLAQACHON BAJARILGAN</h2>
 * 2026-08-20 qoidasi sukut ko'rinishdan yakunlangan yozuvlarni chiqarib
 * tashlashni talab qiladi. Bu yerda u qoidadan ham QAT'IYROQ: standart
 * holat `PENDING`, ya'ni ro'yxat faqat QAROR KUTAYOTGANLARNI ko'rsatadi
 * — tasdiqlangani ham, rad etilgani ham, bekor qilingani ham tashqarida
 * qoladi.
 *
 * <p>⚠️ Va bu filtr SERVERGA boradi (`fetchLeaveRequestPage` da `state`
 * parametri): `REJECTED` tanlansa ro'yxat ularni ROSTDAN qaytaradi,
 * mijozda kesish yo'q. Sahifalangan ro'yxatda buning aksi jimgina bo'sh
 * natija berardi.
 */
export function LeaveRequestsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // ⚠️ N4: `requested_by` ga `LeaveController.currentActorId()` →
  //    `auth.getName()` yoziladi. Bu XODIM ismi emas, aktyor
  //    identifikatori — xaritasiz ustunda xom UUID turardi.
  const actors = useActorDirectory()

  const [state, setState] = useState<LeaveRequestState | null>('PENDING')
  // ⚠️ `null` — sana filtri YO'Q. Sabab sarlavhadagi izohda, o'lchov
  //    bilan. `filter` quyida `null` ni `from`/`to` ni UMUMAN
  //    yubormaslikka o'giradi (`fetchLeaveRequestPage`).
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null)
  const [decision, setDecision] = useState<{ action: LeaveAction; request: LeaveRequest } | null>(
    null,
  )
  const [result, setResult] = useState<OperationResult | null>(null)

  const filter = useMemo(
    () => ({
      personId: null,
      state,
      from: range === null ? null : range[0].format(DATE_FORMAT),
      to: range === null ? null : range[1].format(DATE_FORMAT),
    }),
    [state, range],
  )

  const columns: ColumnsType<LeaveRequest> = [
    {
      title: t('hr.leave.person'),
      key: 'person',
      render: (_value, row) => (
        <Space size={4} direction="vertical">
          <Typography.Link onClick={() => void navigate(`/hr/persons/${row.personId}`)}>
            {row.fullName ?? t('hr.person.unknownName')}
          </Typography.Link>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {row.personnelNo ?? '—'}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: t('hr.leave.type'),
      key: 'type',
      render: (_value, row) => (
        <Space size={6} wrap>
          <span>{row.leaveTypeName ?? row.leaveTypeCode ?? '—'}</span>
          {row.paid ? <Tag color="blue">{t('hr.leave.paid')}</Tag> : null}
        </Space>
      ),
    },
    {
      title: t('hr.leave.range'),
      key: 'range',
      render: (_value, row) => `${row.startsOn} … ${row.endsOn}`,
    },
    { title: t('hr.leave.days'), dataIndex: 'days', key: 'days', align: 'right' },
    {
      title: t('hr.leave.state'),
      key: 'state',
      render: (_value, row) => (
        <Tag color={LEAVE_STATE_COLOR[row.state]}>{t(`leaveState.${row.state}`)}</Tag>
      ),
    },
    {
      title: t('hr.leave.reason'),
      key: 'reason',
      render: (_value, row) => (row.reason === null ? '—' : <ExpandableHint text={row.reason} />),
    },
    {
      title: t('hr.leave.requestedBy'),
      key: 'requestedBy',
      render: (_value, row) => actorLabel(row.requestedBy, actors, t('common.dash')),
    },
    {
      title: '',
      key: 'actions',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_value, row) => (
        // Huquq (`hr.leave.approve` / `hr.leave.request`) va holat sharti
        // (`isPending` / `canCancelLeave`) RowActions ichida filtrlanadi.
        <RowActions
          items={[
            {
              key: 'approve',
              label: t('hr.leave.action.APPROVE.submit'),
              onClick: () => setDecision({ action: 'APPROVE', request: row }),
              permission: 'hr.leave.approve',
              hidden: !isPending(row),
            },
            {
              key: 'reject',
              label: t('hr.leave.action.REJECT.submit'),
              onClick: () => setDecision({ action: 'REJECT', request: row }),
              permission: 'hr.leave.approve',
              danger: true,
              hidden: !isPending(row),
            },
            {
              key: 'cancel',
              label: t('hr.leave.action.CANCEL.submit'),
              onClick: () => setDecision({ action: 'CANCEL', request: row }),
              permission: 'hr.leave.request',
              divider: true,
              hidden: !canCancelLeave(row),
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader title={t('nav.hrLeave')} description={t('pageIntro.hrLeave')} />

      {result ? (
        <div style={{ marginBottom: 16 }}>
          <OperationResultAlert
            partialNote={false}
            result={result}
            closable
            onClose={() => setResult(null)}
          />
        </div>
      ) : null}

      <div style={{ marginBottom: 16 }}>
        <ExpandableHint text={t('hr.leave.listHint')} fontSize={13} />
      </div>

      <DataTable<LeaveRequest>
        queryKey={[...queryKeys.hr.leaveRequests, filter]}
        fetchPage={fetchLeaveRequestPage(filter)}
        columns={columns}
        rowKey={(row) => row.id}
        tableId="hr.leaveRequests"
        emptyTitle={t('hr.leave.emptyList')}
        emptyDescription={t('hr.leave.emptyListHint')}
        toolbarExtra={
          <Space wrap>
            <Select<LeaveRequestState | null>
              allowClear
              placeholder={t('hr.leave.state')}
              value={state}
              onChange={(value) => setState(value ?? null)}
              style={{ width: 170 }}
              options={LEAVE_REQUEST_STATES.map((value) => ({
                value,
                label: t(`leaveState.${value}`),
              }))}
            />
            <DatePicker.RangePicker
              value={range}
              onChange={(value) =>
                setRange(value && value[0] && value[1] ? [value[0], value[1]] : null)
              }
              format={DATE_FORMAT}
            />
          </Space>
        }
      />

      {decision === null ? null : (
        <LeaveDecisionModal
          action={decision.action}
          request={decision.request}
          onClose={() => setDecision(null)}
          onDone={setResult}
        />
      )}
    </>
  )
}
